// src/pages/UserManagement.jsx
import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import {
  Button,
  Paper,
  Stack,
  Typography,
  Select,
  MenuItem,
  Dialog,
  Menu,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Checkbox,
  Breadcrumbs,
  TablePagination,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';

import { Link } from 'react-router-dom';
import { toast } from '../utils/toast';
import { request } from '../utils/request';
import { ColorModeContext } from '../index';
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

const getBadgeStyle = (text, isDark) => {
  const t = (text || '').toLowerCase();
  
  let color = '#616161';
  let bg = '#f5f5f5';

  if (t.includes('admin')) { color = '#1e88e5'; bg = '#e3f2fd'; }
  else if (t.includes('analyst')) { color = '#8e24aa'; bg = '#f3e5f5'; }
  else if (t.includes('guest')) { color = '#757575'; bg = '#f5f5f5'; }
  else if (t.includes('user')) { color = '#039be5'; bg = '#e1f5fe'; }
  else if (t.includes('pre sales') || t.includes('presales')) { color = '#5e35b1'; bg = '#ede7f6'; }
  else if (t.includes('dev')) { color = '#43a047'; bg = '#e8f5e9'; }
  else if (t.includes('hr')) { color = '#f57f17'; bg = '#fffde7'; }
  else if (t.includes('support')) { color = '#d81b60'; bg = '#fce4ec'; }
  else if (t.includes('it')) { color = '#616161'; bg = '#eeeeee'; }

  if (isDark) {
    if (t.includes('admin')) { color = '#90caf9'; bg = 'rgba(144, 202, 249, 0.16)'; }
    else if (t.includes('analyst')) { color = '#ce93d8'; bg = 'rgba(206, 147, 216, 0.16)'; }
    else if (t.includes('guest')) { color = '#bdbdbd'; bg = 'rgba(189, 189, 189, 0.16)'; }
    else if (t.includes('user')) { color = '#81d4fa'; bg = 'rgba(129, 212, 250, 0.16)'; }
    else if (t.includes('pre sales') || t.includes('presales')) { color = '#b39ddb'; bg = 'rgba(179, 157, 219, 0.16)'; }
    else if (t.includes('dev')) { color = '#a5d6a7'; bg = 'rgba(165, 214, 167, 0.16)'; }
    else if (t.includes('hr')) { color = '#ffe082'; bg = 'rgba(255, 224, 130, 0.16)'; }
    else if (t.includes('support')) { color = '#f48fb1'; bg = 'rgba(244, 143, 177, 0.16)'; }
    else if (t.includes('it')) { color = '#bdbdbd'; bg = 'rgba(189, 189, 189, 0.16)'; }
    else { color = '#bdbdbd'; bg = 'rgba(255, 255, 255, 0.08)'; }
  }

  return { color, backgroundColor: bg };
};

export const UserManagement = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];
  
  const checkPerm = (action) => {
    if (isSuperAdmin) return true;

    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        (normalizedPermission.includes('user management') ||
          normalizedPermission.includes('users')) &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canCreate = checkPerm('write');
  const canEdit = checkPerm('update');
  const canDelete = checkPerm('delete');

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isPasswordEdit, setIsPasswordEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await request("role/dropdown", { method: "get" });
      setRoles((data && data.data) || data || []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      showSnack("Failed to fetch roles", "error");
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await request("user", { method: "get" });
      setUsers(Array.isArray(data) ? data : (data && data.data) || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      showSnack("Failed to fetch users", "error");
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  const openDialog = (user = null, editPassword = false) => {
    if (user) {
      if (!canEdit) return;
      setSelectedId(user._id || user.id || null);
      setIsEdit(true);
      setIsPasswordEdit(Boolean(editPassword));
      if (editPassword) {
        setFormData({
          _id: user._id,
          username: user.username || "",
          password: "",
          confirm_password: "",
        });
      } else {
        const copy = { ...user };
        delete copy.password;
        setFormData(copy);
      }
    } else {
      if (!canCreate) return;
      setSelectedId(null);
      setIsEdit(false);
      setIsPasswordEdit(false);
      setFormData({
        status: "active",
        password: "",
        confirm_password: "",
      });
    }
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setIsPasswordEdit(false);
    setIsEdit(false);
    setSelectedId(null);
    setFormData({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      // Limit to numbers only and maximum 10 digits
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    if (name === "emp_id") {
      // Limit employee code to maximum 8 characters/digits
      const cleaned = value.slice(0, 8);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordFieldChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFormData((prev) => ({ ...prev, profile_image_file: file }));
  };

  const showSnack = (message, severity = "success") => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !canCreate) return;
    if (isEdit && !canEdit) return;

    setLoading(true);

    try {
      if (!isPasswordEdit) {
        if (!String(formData.first_name || '').trim()) {
          showSnack("First Name is required", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.last_name || '').trim()) {
          showSnack("Last Name is required", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.username || '').trim()) {
          showSnack("Username is required", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.email || '').trim()) {
          showSnack("Email is required", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.designation || '').trim()) {
          showSnack("Designation is required", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.mobile || '').trim()) {
          showSnack("Mobile Number is required", "error");
          setLoading(false);
          return;
        }
        if (String(formData.mobile || '').trim().length > 10) {
          showSnack("Mobile Number cannot exceed 10 digits", "error");
          setLoading(false);
          return;
        }
        if (!String(formData.emp_id || '').trim()) {
          showSnack("Employee ID is required", "error");
          setLoading(false);
          return;
        }
        if (String(formData.emp_id || '').trim().length > 8) {
          showSnack("Employee ID cannot exceed 8 digits", "error");
          setLoading(false);
          return;
        }
        if (!formData.roleId) {
          showSnack("Role is required", "error");
          setLoading(false);
          return;
        }
        if (!formData.status) {
          showSnack("Status is required", "error");
          setLoading(false);
          return;
        }
      }

      if (
        formData.password ||
        formData.confirm_password ||
        isPasswordEdit ||
        (!isEdit && formData.password !== undefined)
      ) {
        if (!formData.password) {
          showSnack("Password is required", "error");
          setLoading(false);
          return;
        }
        if (
          formData.confirm_password !== undefined &&
          formData.password !== formData.confirm_password
        ) {
          showSnack("Passwords do not match", "error");
          setLoading(false);
          return;
        }
      }

      const requestBody = new FormData();
      const commonFields = [
        "first_name",
        "last_name",
        "username",
        "email",
        "department",
        "designation",
        "roleId",
        "mobile",
        "emp_id",
        "status",
      ];

      if (isPasswordEdit) {
        requestBody.append("password", formData.password);
      } else {
        commonFields.forEach((field) => {
          if (typeof formData[field] !== "undefined" && formData[field] !== null) {
            requestBody.append(field, formData[field]);
          }
        });
      }

      if (formData.password && !isPasswordEdit) {
        requestBody.append("password", formData.password);
      }

      if (formData.profile_image_file) {
        requestBody.append("profile_image", formData.profile_image_file);
      }

      if (isEdit && !isPasswordEdit) {
        await request("user", {
          method: "put",
          params: selectedId,
          body: requestBody,
        });
        toast("User updated", { variant: "success" });
        showSnack("User updated successfully", "success");
      } else if (isEdit && isPasswordEdit) {
        await request("user", {
          method: "put",
          params: selectedId,
          body: requestBody,
        });
        toast("Password updated", { variant: "success" });
        showSnack("Password updated successfully", "success");
      } else {
        await request("register", {
          method: "post",
          body: requestBody,
        });
        toast("User created", { variant: "success" });
        showSnack("User created successfully", "success");
      }

      await fetchUsers();
      closeDialog();
    } catch (err) {
      console.error("Save user error:", err);
      const message = err?.message || "Error saving user";
      toast("Error saving user", { variant: "error" });
      showSnack(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    if (!canDelete) return;
    setDeletingUser(user);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingUser(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser || !deletingUser._id) return;
    setDeleting(true);
    try {
      await request("user", { method: "delete", params: deletingUser._id });
      toast("User deleted", { variant: "success" });
      showSnack(`User "${deletingUser.username || deletingUser.email}" deleted.`, "success");
      await fetchUsers();
      handleCancelDelete();
    } catch (err) {
      console.error("Error deleting user:", err);
      toast("Error deleting user", { variant: "error" });
      showSnack("Failed to delete user", "error");
      setDeleting(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const timeA = a.createdAt
        ? new Date(a.createdAt).getTime()
        : (a._id && String(a._id).length >= 8 ? parseInt(String(a._id).substring(0, 8), 16) * 1000 : 0);
      const timeB = b.createdAt
        ? new Date(b.createdAt).getTime()
        : (b._id && String(b._id).length >= 8 ? parseInt(String(b._id).substring(0, 8), 16) * 1000 : 0);
      return timeB - timeA;
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return sorted;

    return sorted.filter((u) => {
      const username = (u.username || '').toLowerCase();
      const firstName = (u.first_name || '').toLowerCase();
      const lastName = (u.last_name || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const reverseFullName = `${lastName} ${firstName}`.trim();
      const email = (u.email || '').toLowerCase();
      const empId = (u.emp_id || u.employee_id || '').toLowerCase();
      const mobile = (u.mobile || u.mobile_number || u.phone || '').toLowerCase();
      const department = (u.department || '').toLowerCase();
      const roleName = (roles.find(r => r._id === u.roleId)?.role || '').toLowerCase();

      return (
        username.includes(q) ||
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q) ||
        reverseFullName.includes(q) ||
        email.includes(q) ||
        empId.includes(q) ||
        mobile.includes(q) ||
        department.includes(q) ||
        roleName.includes(q)
      );
    });
  }, [sorted, searchQuery, roles]);

  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, users.length]);

  const handleChangePage = (event, nextPage) => setPage(nextPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleIds = useMemo(() => filteredUsers.map(u => String(u._id)), [filteredUsers]);

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelectedIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(visibleIds.slice());
    } else {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  const isAllSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length !== 1) return;
    const user = filteredUsers.find(u => String(u._id) === selectedIds[0]);
    if (user) {
      openDialog(user);
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => request('user', { method: 'delete', params: id })));
      showSnack(`${selectedIds.length} user(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchUsers();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected users.', 'error');
      setBulkDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    '& .MuiInputBase-root': { 
      backgroundColor: isDark ? '#1E293B' : '#ffffff',
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': { 
      color: isDark ? '#F8FAFC' : '#0F172A',
      fontSize: '14px',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: isDark ? '0 0 0 100px #1E293B inset !important' : '0 0 0 100px #ffffff inset !important',
      WebkitTextFillColor: isDark ? '#F8FAFC !important' : '#0F172A !important',
      caretColor: isDark ? '#F8FAFC' : '#0F172A',
      borderRadius: 'inherit'
    },
    '& input::placeholder': {
      color: '#94A3B8',
      opacity: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
      borderWidth: '1px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.2) !important' : '#CBD5E1 !important' 
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3B82F6 !important',
      borderWidth: '1px !important',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    '& .MuiSvgIcon-root': { color: '#94A3B8', fontSize: '20px' }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link
            to="/home"
            className="global-link"
            style={{
              textDecoration: "none",
              color: isDark ? "#90caf9" : "#2d9cdb",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = isDark ? "#fff" : "#0b1d51";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = isDark ? "#90caf9" : "#2d9cdb";
            }}
          >
            Dashboard
          </Link>
          <Typography className="global-section-title" sx={{ fontSize: '0.95rem', margin: 0, color: isDark ? '#fff' : '#333' }}>
            User Management
          </Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name, email, emp ID, mobile..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: isDark ? '#94A3B8' : '#64748B' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    sx={{ color: isDark ? '#94A3B8' : '#64748B' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            className="global-input"
            sx={{
              minWidth: { xs: 220, sm: 300, md: 340 },
              '& .MuiInputBase-root': {
                backgroundColor: isDark ? '#1E293B' : '#ffffff',
                borderRadius: '8px',
                height: '38px',
                fontSize: '0.875rem',
                color: isDark ? '#F8FAFC' : '#0F172A',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2) !important' : '#CBD5E1 !important',
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3B82F6 !important',
              },
            }}
          />
          {canCreate && (
            <Button
              variant="contained"
              className="global-button global-button-primary"
              onClick={() => openDialog(null, false)}
              sx={{ px: 2, height: '38px', whiteSpace: 'nowrap' }}
            >
              Add User
            </Button>
          )}
        </Stack>
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" alignItems="center" gap={1}>
          <Typography sx={{ color: isDark ? '#fff' : 'inherit' }}>{selectedIds.length} selected</Typography>
          {canEdit && (
            <Button size="small" variant="outlined" className="global-button-outlined" onClick={handleEditSelected} disabled={selectedIds.length !== 1} sx={{ borderColor: isDark ? '#fff' : undefined, color: isDark ? '#fff' : undefined }}>Edit</Button>
          )}
          {canDelete && (
            <Button size="small" variant="contained" className="global-button-danger" onClick={handleDeleteSelected}>
              Delete
            </Button>
          )}
          <Button size="small" className="global-button-text" onClick={() => setSelectedIds([])} sx={{ color: isDark ? '#fff' : undefined }}>Clear</Button>
        </Box>
      )}

      <Paper 
        elevation={0} 
        className="global-card"
        sx={{ 
          mb: 2, 
          height: 'calc(100vh - 170px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#111827' : '#ffffff',
          borderRadius: '16px',
          boxShadow: isDark ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.05)',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            height: 'calc(100% - 52px)', 
            overflow: 'auto', 
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)' }
          }}
        >
          <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 1200, width: '100%' }}>
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  p={2}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                    borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
                    width: 48,
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all users' }}
                    sx={{ color: isDark ? '#fff' : undefined }}
                  />
                </Box>

                {[
                  { label: '#', width: 40, align: 'left' },
                  { label: 'Username', width: 150, align: 'left' },
                  { label: 'Emp ID', width: 100, align: 'left' },
                  { label: 'First', width: 120, align: 'left' },
                  { label: 'Last', width: 120, align: 'left' },
                  { label: 'Email', width: 220, align: 'left' },
                  { label: 'Mobile', width: 120, align: 'left' },
                  { label: 'Role', width: 120, align: 'left' },
                  { label: 'Department', width: 120, align: 'left' },
                  ...(canEdit || canDelete ? [{ label: 'Actions', width: 80, align: 'center' }] : [])
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={2}
                    textAlign={col.align}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      width: col.width,
                      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                      color: isDark ? '#D1D5DB' : '#111827',
                      borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #E5E7EB',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {filteredUsers.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={canEdit || canDelete ? 11 : 10} p={4} textAlign="center" className="table-empty" sx={{ color: isDark ? '#9CA3AF' : '#64748B' }}>
                    {searchQuery ? (
                      <Stack spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2" sx={{ color: isDark ? '#E2E8F0' : '#334155' }}>
                          No users found matching &ldquo;<strong>{searchQuery}</strong>&rdquo;
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setSearchQuery('')}
                          sx={{ textTransform: 'none', color: '#3B82F6', fontSize: '0.85rem' }}
                        >
                          Clear Search
                        </Button>
                      </Stack>
                    ) : (
                      'No users found.'
                    )}
                  </Box>
                </Box>
              ) : (
                paginatedUsers.map((u, idx) => {
                  const roleName = roles.find(r => r._id === u.roleId)?.role || '—';
                  return (
                    <Box 
                      component="tr" 
                      key={u._id}
                      sx={{
                        borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #F3F4F6',
                        '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFB' },
                        transition: 'background-color 0.2s',
                        height: '64px'
                      }}
                    >
                      <Box component="td" p={2} sx={{ width: 48, textAlign: 'center', verticalAlign: 'middle' }}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(String(u._id))}
                          onChange={() => toggleSelect(u._id)}
                          inputProps={{ 'aria-label': `select user ${u._id}` }}
                          sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : undefined }}
                        />
                      </Box>

                      <Box component="td" p={2} sx={{ color: isDark ? '#F9FAFB' : '#111827', verticalAlign: 'middle', fontSize: '0.9rem' }}>{page * rowsPerPage + idx + 1}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#F9FAFB' : '#111827', verticalAlign: 'middle', fontWeight: 600, fontSize: '0.9rem' }}>{u.username}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontSize: '0.9rem' }}>{u.emp_id || '—'}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontSize: '0.9rem' }}>{u.first_name}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontSize: '0.9rem' }}>{u.last_name}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontSize: '0.9rem', wordBreak: 'break-all' }}>{u.email}</Box>
                      <Box component="td" p={2} sx={{ color: isDark ? '#D1D5DB' : '#4B5563', verticalAlign: 'middle', fontSize: '0.9rem' }}>{u.mobile}</Box>
                      <Box component="td" p={2} sx={{ verticalAlign: 'middle' }}>
                        <Box
                          component="span"
                          sx={{
                            ...getBadgeStyle(roleName, isDark),
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'inline-block'
                          }}
                        >
                          {roleName}
                        </Box>
                      </Box>
                      <Box component="td" p={2} sx={{ verticalAlign: 'middle' }}>
                        <Box
                          component="span"
                          sx={{
                            ...getBadgeStyle(u.department || '—', isDark),
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'inline-block'
                          }}
                        >
                          {u.department || '—'}
                        </Box>
                      </Box>

                      {(canEdit || canDelete) && (
                        <Box component="td" p={2} textAlign="center" sx={{ verticalAlign: 'middle' }}>
                          <IconButton
                            size="small"
                            aria-label="more"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setMenuUser(u);
                            }}
                            className="global-icon-button"
                            sx={{ color: isDark ? '#F9FAFB' : 'inherit' }}
                          >
                            <MoreVertIcon fontSize="small" className="text-secondary" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>

        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ flexShrink: 0 }}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => { setAnchorEl(null); setMenuUser(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: 'global-card', sx: { backgroundColor: isDark ? '#111827' : '#fff', color: isDark ? '#F9FAFB' : 'inherit', backgroundImage: 'none' } }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                if (menuUser) openDialog(menuUser);
                setAnchorEl(null);
                setMenuUser(null);
              }}
            >
              Edit
            </MenuItem>
          )}
          {canEdit && (
            <MenuItem
              onClick={() => {
                if (menuUser) openDialog(menuUser, true);
                setAnchorEl(null);
                setMenuUser(null);
              }}
            >
              Reset Password
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={() => {
                if (menuUser) handleDeleteClick(menuUser);
                setAnchorEl(null);
                setMenuUser(null);
              }}
              className="global-text-critical"
            >
              Delete
            </MenuItem>
          )}
        </Menu>
      </Paper>

      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="User"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      <Dialog 
        open={open} 
        onClose={closeDialog} 
        fullWidth 
        maxWidth={isPasswordEdit ? "sm" : "md"}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            overflow: 'hidden',
            margin: { xs: '16px', md: '32px' },
            height: { xs: 'calc(100% - 32px)', md: 'auto' },
            maxHeight: { xs: 'calc(100% - 32px)', md: 'calc(100% - 64px)' }
          }
        }}
        PaperProps={{ 
          sx: { 
            backgroundColor: isDark ? '#111827' : '#fff', 
            color: isDark ? '#F9FAFB' : 'inherit',
            backgroundImage: 'none',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
          } 
        }}
      >
        <Box display="flex" sx={{ height: { xs: '100%', md: isPasswordEdit ? 'auto' : '650px' }, maxHeight: { xs: '100%', md: '85vh' }, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* LEFT SIDE (Fixed) */}
          <Box 
            sx={{ 
              width: { xs: '100%', md: '35%' }, 
              background: isDark ? 'linear-gradient(180deg, #0b1120 0%, #151C2B 100%)' : 'linear-gradient(180deg, #F8FAFC 0%, #E0F2FE 100%)', 
              p: 4,
              display: isPasswordEdit ? 'none' : { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              overflow: 'hidden',
              borderRight: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
              flexShrink: 0
            }}
          >
             {/* Push content down slightly */}
             <Box sx={{ mt: isPasswordEdit ? 4 : 10, display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
               {/* Icon Card */}
               <Box 
                 sx={{ 
                   width: 88, 
                   height: 88, 
                   backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#ffffff', 
                   borderRadius: '20px', 
                   display: 'flex', 
                   justifyContent: 'center', 
                   alignItems: 'center',
                   mb: 4,
                   boxShadow: isDark ? 'none' : '0 12px 36px rgba(2, 132, 199, 0.15)',
                   border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none'
                 }}
               >
                 <PersonAddIcon sx={{ fontSize: 44, color: isDark ? '#60A5FA' : '#0284C7' }} />
               </Box>
               
               {/* Title (bypassing global-h4 inheritance) */}
               <Typography component="div" sx={{ fontSize: '32px', fontWeight: 800, color: isDark ? '#F8FAFC' : '#0F172A', mb: 2, textAlign: 'center' }}>
                 {isPasswordEdit ? "Reset Password" : isEdit ? "Edit User" : "Add User"}
               </Typography>
               
               {/* Subtitle */}
               <Typography component="div" sx={{ color: isDark ? '#94A3B8' : '#475569', lineHeight: 1.6, textAlign: 'center', fontSize: '15px', px: 2 }}>
                 {isPasswordEdit 
                   ? "Update the user's password securely to ensure system access is protected." 
                   : "Create a new user account to provide system access and permissions."}
               </Typography>
             </Box>
             
             {/* Decorative background vectors (mimicking waves/cards) */}
             <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '250px', zIndex: 1, overflow: 'hidden' }}>
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: -50, left: -20, width: '120%', opacity: 0.4 }}>
                  <path fill={isDark ? '#1E3A8A' : '#BAE6FD'} d="M38.1,-63.9C49.9,-55.5,60.2,-44.6,67.8,-31.6C75.4,-18.6,80.3,-3.4,78.2,11.2C76.1,25.8,67,39.8,55.1,49.8C43.2,59.8,28.5,65.8,13.5,69.5C-1.5,73.2,-16.8,74.6,-30.3,69.5C-43.8,64.4,-55.5,52.8,-63.5,39.3C-71.5,25.8,-75.8,10.4,-74.6,-4.5C-73.4,-19.4,-66.7,-33.8,-56.3,-44.4C-45.9,-55,-31.8,-61.8,-18.2,-65.4C-4.6,-69,9.5,-69.4,23.3,-67.2C37.1,-65,50.7,-60.2,38.1,-63.9Z" transform="translate(100 100) scale(1.2)" />
                </svg>
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: -80, right: -40, width: '100%', opacity: 0.3 }}>
                  <path fill={isDark ? '#1D4ED8' : '#7DD3FC'} d="M47.7,-72.6C62.1,-64.1,74.2,-50.7,81.4,-35C88.6,-19.3,90.9,-1.3,86.2,14.7C81.5,30.7,69.8,44.7,55.4,54.7C41,64.7,23.9,70.7,6.4,72.4C-11.1,74.1,-28.9,71.5,-44,62.8C-59.1,54.1,-71.5,39.3,-78.3,21.8C-85.1,4.3,-86.3,-15.9,-79.1,-32.4C-71.9,-48.9,-56.3,-61.7,-40.5,-69.6C-24.7,-77.5,-8.7,-80.5,6.7,-81C22.1,-81.5,44.2,-79.5,47.7,-72.6Z" transform="translate(100 100) scale(1.1)" />
                </svg>
                {/* White overlapping abstract cards to match the reference */}
                <Box sx={{ position: 'absolute', bottom: -20, left: '10%', width: 140, height: 180, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.4)', borderRadius: '24px', transform: 'rotate(-15deg)', backdropFilter: 'blur(4px)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: '25%', width: 160, height: 200, backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.7)', borderRadius: '24px', transform: 'rotate(5deg)', backdropFilter: 'blur(8px)' }} />
             </Box>
          </Box>

          {/* RIGHT SIDE */}
          <Box sx={{ width: { xs: '100%', md: isPasswordEdit ? '100%' : '65%' }, position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <IconButton onClick={closeDialog} sx={{ position: 'absolute', top: 16, right: 16, color: '#9CA3AF', zIndex: 10 }}>
              <CloseIcon />
            </IconButton>
             
            {/* Scrollable Content */}
            <Box component="form" id="user-form" onSubmit={handleSubmit} noValidate sx={{ 
              p: { xs: 3, md: 5 }, 
              pt: { xs: 6, md: 5 },
              flex: 1, 
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
            }}>
              {isPasswordEdit && (
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A', mb: 3 }}>
                  Reset Password
                </Typography>
              )}
              <Grid container spacing={3}>
                
                {isPasswordEdit ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                        New Password <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        name="password"
                        placeholder="Enter password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password || ""}
                        onChange={handlePasswordFieldChange}
                        sx={inputStyle}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  if (formData.password) {
                                    navigator.clipboard.writeText(formData.password);
                                    showSnack("Password copied to clipboard", "success");
                                  }
                                }}
                                title="Copy Password"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                        Confirm Password <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        name="confirm_password"
                        placeholder="Confirm password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirm_password || ""}
                        onChange={handlePasswordFieldChange}
                        sx={inputStyle}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                {showConfirmPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    {[
                      { name: "first_name", label: "First Name", placeholder: "Enter first name", req: true },
                      { name: "last_name", label: "Last Name", placeholder: "Enter last name", req: true },
                      { name: "username", label: "Username", placeholder: "Enter username", req: true },
                      { name: "email", label: "Email", placeholder: "Enter email address", req: true, type: "email", icon: <MailOutlineIcon fontSize="small" /> },
                      { name: "department", label: "Department", placeholder: "Enter department" },
                      { name: "designation", label: "Designation", placeholder: "Enter designation", req: true },
                      { name: "mobile", label: "Mobile Number", placeholder: "Enter mobile number", req: true, icon: <PhoneIcon fontSize="small" />, maxLength: 10, inputMode: "numeric" },
                      { name: "emp_id", label: "Employee ID", placeholder: "Enter employee ID", req: true, maxLength: 8 },
                    ].map((field, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                          {field.label} {field.req && <span style={{ color: '#EF4444' }}>*</span>}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          name={field.name}
                          placeholder={field.placeholder}
                          type={field.type || "text"}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          inputProps={{
                            ...(field.maxLength ? { maxLength: field.maxLength } : {}),
                            ...(field.inputMode ? { inputMode: field.inputMode } : {}),
                          }}
                          sx={inputStyle}
                          InputProps={field.icon ? {
                            startAdornment: <InputAdornment position="start">{field.icon}</InputAdornment>
                          } : undefined}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                        Role <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        name="roleId"
                        value={formData.roleId || ""}
                        onChange={handleChange}
                        displayEmpty
                        sx={inputStyle}
                      >
                        <MenuItem value="" disabled><span style={{ color: '#9CA3AF' }}>Select role</span></MenuItem>
                        {roles.map((opt) => (
                          <MenuItem key={opt._id} value={opt._id}>{opt.role}</MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                        Status <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        name="status"
                        value={formData.status || ""}
                        onChange={handleChange}
                        displayEmpty
                        sx={{
                          ...inputStyle,
                          ...( !isEdit && {
                            backgroundColor: isDark ? '#334155' : '#F1F5F9',
                            '& .MuiSelect-select.Mui-disabled': {
                              WebkitTextFillColor: '#94A3B8',
                            }
                          })
                        }}
                        disabled={!isEdit}
                      >
                        <MenuItem value="" disabled><span style={{ color: '#9CA3AF' }}>Select status</span></MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                        <MenuItem value="deleted">Deleted</MenuItem>
                      </Select>
                    </Grid>

                    {!isEdit && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                            Password <span style={{ color: '#EF4444' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="password"
                            placeholder="Enter password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password || ""}
                            onChange={handlePasswordFieldChange}
                            sx={inputStyle}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => {
                                      if (formData.password) {
                                        navigator.clipboard.writeText(formData.password);
                                        showSnack("Password copied to clipboard", "success");
                                      }
                                    }}
                                    title="Copy Password"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                            Confirm Password <span style={{ color: '#EF4444' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="confirm_password"
                            placeholder="Confirm password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirm_password || ""}
                            onChange={handlePasswordFieldChange}
                            sx={inputStyle}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, display: 'block', color: isDark ? '#E2E8F0' : '#1E293B', fontSize: '13px' }}>
                        Profile Image (optional)
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                        <Button 
                          variant="contained" 
                          component="label"
                          size="small"
                          sx={{ backgroundColor: '#F1F5F9', color: '#334155', textTransform: 'none', boxShadow: 'none', border: '1px solid #E2E8F0', fontWeight: 600, '&:hover': { backgroundColor: '#E2E8F0', boxShadow: 'none' } }}
                        >
                          Choose File
                          <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                        <Typography variant="body2" sx={{ color: formData.profile_image_file ? '#10B981' : '#64748B' }}>
                          {formData.profile_image_file ? formData.profile_image_file.name : "No file chosen"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                        Supported formats: JPG, PNG (Max size: 2MB)
                      </Typography>
                      
                      <Box mt={1}>
                        {formData.profile_image_file ? (
                          <img alt="preview" src={URL.createObjectURL(formData.profile_image_file)} style={{ maxWidth: 80, maxHeight: 80, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                        ) : isEdit && formData.profile_image ? (
                          <img alt="current" src={formData.profile_image} style={{ maxWidth: 80, maxHeight: 80, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                        ) : null}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            {/* Bottom Actions Footer */}
            <Box sx={{ 
              p: 3, 
              px: { xs: 3, md: 5 }, 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2, 
              borderTop: '1px solid var(--hairline)', 
              bgcolor: 'var(--canvas)', 
              flexShrink: 0 
            }}>
              <Button 
                onClick={closeDialog} 
                variant="outlined"
                sx={{
                  color: 'var(--vault-navy)',
                  borderColor: 'var(--hairline)',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'var(--canvas)',
                    borderColor: 'var(--slate)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                form="user-form"
                variant="contained" 
                disabled={loading}
                sx={{
                  bgcolor: 'var(--edge-blue)',
                  color: '#FFF',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  px: 4,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'var(--edge-blue)',
                    opacity: 0.9,
                    boxShadow: 'none',
                  },
                  '&:focus': {
                    boxShadow: '0 0 0 3px var(--blue-tint)',
                  },
                }}
              >
                {loading ? 'Processing...' : (isPasswordEdit ? "Update Password" : isEdit ? "Save Changes" : "Create User")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <CommonDeleteModal
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        entityName={deletingUser ? deletingUser.username || deletingUser.email : ""}
        entityType="User"
      />

      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: "100%", fontSize: "0.9rem" }} className="global-alert">
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};